import type { ReactNode } from "react";
import { Button } from "../ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../ui/tooltip";
import {
  IconBookStack,
  IconAudio,
  IconInbox,
  IconKanban,
  IconLayoutGrid,
  IconLibrary,
  IconMapPin,
  IconPublish,
  IconSettings,
  IconSparkles,
  IconThemes,
  IconTimeline,
  IconUser,
  IconUsers,
} from "./icons";
import type { WorkbenchViewId } from "../../../shared/bookType";

export type WorkbenchView = WorkbenchViewId;

const ITEMS: Array<{ view: WorkbenchView; label: string; icon: ReactNode }> = [
  { view: "book", label: "Book", icon: <IconBookStack /> },
  { view: "planning", label: "Planning", icon: <IconKanban /> },
  { view: "plot", label: "Plot Grid", icon: <IconLayoutGrid /> },
  { view: "codex", label: "Codex", icon: <IconSparkles /> },
  { view: "characters", label: "Characters", icon: <IconUser /> },
  { view: "locations", label: "Locations", icon: <IconMapPin /> },
  { view: "timeline", label: "Timeline", icon: <IconTimeline /> },
  { view: "themes", label: "Themes & Motifs", icon: <IconThemes /> },
  { view: "research", label: "Research", icon: <IconLibrary /> },
  { view: "formatter", label: "Formatter", icon: <IconPublish /> },
  { view: "audio", label: "Audio", icon: <IconAudio /> },
  { view: "beta", label: "Beta readers", icon: <IconUsers size={20} /> },
  { view: "settings", label: "Settings", icon: <IconSettings /> },
];

const ADMIN_ITEM = {
  view: "adminFeedback" as const,
  label: "Feedback admin",
  icon: <IconInbox />,
};

export function ActivityBar({
  activeView,
  focusMode,
  showAdminFeedback = false,
  visibleViews,
  onSelectView,
}: {
  activeView: WorkbenchView;
  focusMode: boolean;
  /** Only sys_admin cloud users see the feedback admin view. */
  showAdminFeedback?: boolean;
  /** When set, hide views that are not in this list (book-type gating). */
  visibleViews?: readonly WorkbenchView[];
  onSelectView: (view: WorkbenchView) => void;
}) {
  if (focusMode) return null;

  const allowed = visibleViews ? new Set(visibleViews) : null;
  let items = allowed ? ITEMS.filter((item) => allowed.has(item.view)) : ITEMS;
  if (showAdminFeedback) {
    const settingsIdx = items.findIndex((item) => item.view === "settings");
    if (settingsIdx >= 0) {
      items = [...items.slice(0, settingsIdx), ADMIN_ITEM, ...items.slice(settingsIdx)];
    } else {
      items = [...items, ADMIN_ITEM];
    }
  }

  return (
    <TooltipProvider delayDuration={250}>
      <nav
        className="flex w-full flex-col items-center gap-1.5 overflow-hidden px-1 py-2"
        aria-label="Primary views"
      >
        {items.map((item) => {
          const active = activeView === item.view;
          return (
            <Tooltip key={item.view}>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="size-8 text-muted-foreground aria-pressed:border-primary/40 aria-pressed:bg-primary/15 aria-pressed:text-primary aria-pressed:ring-1 aria-pressed:ring-primary/20 aria-pressed:ring-inset"
                  aria-label={item.label}
                  aria-pressed={active}
                  onClick={() => onSelectView(item.view)}
                >
                  {item.icon}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="right" sideOffset={10}>
                {item.label}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </nav>
    </TooltipProvider>
  );
}
